// src/pages/AddFarmPage1.jsx

import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import InputAdornment from "@mui/material/InputAdornment";
import AgricultureIcon from "@mui/icons-material/Agriculture"; // ✅ Works
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Button from "@mui/material/Button";
import { useMediaQuery } from "@mui/material";
import "./TrialAddFarm.css";
import Tooltip from "@mui/material/Tooltip"; // Import Tooltip component
import { getUserEmail } from "../../../utils/auth";
import BASE_URL from "../../../config/apiConfig";

/* global google */

const TrialAddFarm = () => {
  const navigate = useNavigate();

  const mapRef = useRef(null);
  const searchBoxRef = useRef(null);
  const polygonCoords = useRef([]);
  const polygonRef = useRef(null);
  const map = useRef(null);
  const DEFAULT_LOCATION = { lat: 37.7749, lng: -122.4194 };

  const [farmName, setFarmName] = useState("");
  const [sowingDate, setSowingDate] = useState("");
  const [cropType, setCropType] = useState("plantation");
  const [crops, setCrops] = useState([]);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [selectedArea, setSelectedArea] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [SearchErrorMessage, setSearchErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [spinnerLoading, setSpinnerLoading] = useState(false);

  // const [totalPrice, setTotalPrice] = useState(0);
  // const [paymentFrequency, setPaymentFrequency] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [hovered, setHovered] = useState(true);

  useEffect(() => {
    const loadGoogleMapsScript = () => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=geometry,places,marker`;
      script.async = true;
      script.defer = true;
      script.onload = () =>
        window.google
          ? initMap()
          : setErrorMessage("Google Maps failed to load.");
      script.onerror = () => setErrorMessage("Failed to load Google Maps.");
      document.body.appendChild(script);

      return () => document.body.removeChild(script);
    };

    loadGoogleMapsScript();
  }, []);

  const initMap = () => {
    getUserLocation()
      .then((loc) => initializeMap(loc))
      .catch(() => initializeMap(DEFAULT_LOCATION));
  };

  const getUserLocation = () =>
    new Promise((resolve, reject) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (pos) =>
            resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          async () => {
            try {
              const ipLoc = await axios.get("https://ipapi.co/json/");
              resolve({ lat: ipLoc.data.latitude, lng: ipLoc.data.longitude });
            } catch {
              reject();
            }
          },
        );
      } else {
        reject();
      }
    });

  const initializeMap = (center) => {
    map.current = new google.maps.Map(mapRef.current, {
      center,
      zoom: 15,
      mapTypeId: google.maps.MapTypeId.HYBRID,
      mapId: "520ede0eb4624c69",
    });

    polygonRef.current = new google.maps.Polygon({
      paths: polygonCoords.current,
      strokeColor: "#FFD21F",
      strokeOpacity: 1,
      strokeWeight: 4,
      fillColor: "#8BC34A",
      fillOpacity: 0.22,
      geodesic: true,
    });

    polygonRef.current.setMap(map.current);

    map.current.addListener("click", (e) => {
      addMarker(e.latLng);
      addPolygonPoint(e.latLng);
    });

    const input = searchBoxRef.current;
    const searchBox = new google.maps.places.SearchBox(input);
    searchBox.addListener("places_changed", () => {
      const places = searchBox.getPlaces();
      if (!places.length) return;

      const bounds = new google.maps.LatLngBounds();
      places.forEach((place) => {
        if (place.geometry?.location) {
          bounds.extend(place.geometry.location);
          map.current.setCenter(place.geometry.location);
          map.current.setZoom(15);
        }
      });
      map.current.fitBounds(bounds);
    });
  };

  const addMarker = (location) => {
    map.current.markers ||= [];
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: map.current,
      position: location,
    });
    map.current.markers.push(marker);
  };

  const addPolygonPoint = (location) => {
    polygonCoords.current.push({ lat: location.lat(), lng: location.lng() });
    polygonRef.current.setPath(polygonCoords.current);
    updateArea();
  };

  const updateArea = () => {
    if (polygonCoords.current.length < 3) return setSelectedArea(0);
    const polygon = new google.maps.Polygon({ paths: polygonCoords.current });
    const sqm = google.maps.geometry.spherical.computeArea(polygon.getPath());
    setSelectedArea((sqm / 10000).toFixed(2));
  };

  const handleGeocode = () => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      { address: searchBoxRef.current.value },
      (results, status) => {
        if (status === "OK" && results[0]) {
          const loc = results[0].geometry.location;
          map.current.setCenter(loc);
          addMarker(loc);
        } else {
          setSearchErrorMessage(
            "Location not found. Please enter a precise address or select one from the suggestions.",
          );
        }
      },
    );
  };

  const clearAll = () => {
    map.current?.markers?.forEach((m) => (m.map = null));
    map.current.markers = [];
    polygonCoords.current = [];
    polygonRef.current?.setPath([]);
    setSelectedArea(0);
    setErrorMessage("");
  };

  useEffect(() => {
    const fetchCrops = async () => {
      if (!cropType) return;
      setLoading(true);
      try {
        const res = await fetch(
          `${BASE_URL}/api/${cropType === "plantation" ? "plantation-crops" : "field-crops"}`,
        );
        const data = await res.json();
        const formatted = data.map((c) => ({
          id: c.crop_id,
          name: c.crop_name,
          perHectarePrice: c.per_hectare_price,
        }));
        setCrops(formatted);
        setSelectedCrop(formatted[0] || null);
      } catch {
        setCrops([]);
        setSelectedCrop(null);
      } finally {
        setLoading(false);
      }
    };
    fetchCrops();
  }, [cropType]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchCropCode = async (cropName) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/api/user/farm-management/get-crop-code`,
        {
          cropName,
        },
      );
      if (response.data.success) {
        return response.data.cropCode;
      } else {
        setErrorMessage(response.data.message || "Failed to fetch crop code.");
        return null;
      }
    } catch (error) {
      console.error("Failed to fetch crop code:", error);
      setErrorMessage("Failed to fetch crop code. Please try again later.");
      return null;
    }
  };

  const downloadMapScreenshot = async () => {
    if (!polygonCoords.current.length) return null;

    // Ensure the polygon is closed by repeating the first coordinate at the end
    const closedCoords = [...polygonCoords.current, polygonCoords.current[0]];

    const pathString = closedCoords
      .map((coord) => `${coord.lat},${coord.lng}`)
      .join("|");

    const visibleCoords = closedCoords
      .map((coord) => `${coord.lat},${coord.lng}`)
      .join("|");

    // Add plant emoji (🌱) at each point as marker
    const markers = closedCoords
      .map(
        (coord) =>
          `&markers=http://maps.google.com/mapfiles/kml/pal4/icon15.png|${coord.lat},${coord.lng}`,
      )
      .join("");

    // Construct the static map URL
    const staticMapUrl =
      `https://maps.googleapis.com/maps/api/staticmap?` +
      `visible=${visibleCoords}` +
      `&size=640x640&scale=2` +
      `&maptype=hybrid` +
      `&path=color:0x000000ff|weight:6|fillcolor:0x66bb6a55|${pathString}` + // Dark border with transparent light green fill
      markers +
      `&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;

    try {
      const response = await fetch(staticMapUrl);
      const blob = await response.blob();
      const formData = new FormData();
      formData.append("image", blob, "farm_map.png");

      const uploadRes = await fetch(
        `${BASE_URL}/api/user/farm-management/upload-map-image`, // Update the endpoint to your backend
        {
          method: "POST",
          body: formData,
        },
      );

      const data = await uploadRes.json();
      if (data?.image_url) {
        console.log("Image saved at:", data.image_url);
        return data.image_url; // Return the URL of the uploaded image
      } else {
        alert("Upload failed");
        return null;
      }
    } catch (err) {
      console.error("Upload error:", err);
      alert("Failed to generate or upload map image.");
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!farmName || !sowingDate || !selectedCrop) {
      setErrorMessage("Please complete all fields.");
      return;
    }

    setErrorMessage("");
    setSpinnerLoading(true); // Show spinner while processing

    try {
      const cropCode = await fetchCropCode(selectedCrop.name);
      if (!cropCode) {
        setSpinnerLoading(false);
        return;
      }

      if (!polygonCoords.current.length) {
        setErrorMessage("Please mark your farm area on the map.");
        setSpinnerLoading(false);
        return;
      }

      const areaInHectares = (
        google.maps.geometry.spherical.computeArea(
          polygonRef.current.getPath(),
        ) / 10000
      ).toFixed(2);

      const mapImageUrl = await downloadMapScreenshot();
      if (!mapImageUrl) {
        setErrorMessage("Failed to generate map image.");
        setSpinnerLoading(false);
        return;
      }

      if (areaInHectares > 2) {
        setErrorMessage("Trial version is limited up to 2 hectares.");
        setSpinnerLoading(false);
        return;
      }

      const coordinates = polygonCoords.current.map((coord) => ({
        lat: coord.lat,
        lng: coord.lng,
      }));

      const fieldData = {
        CropCode: cropCode,
        FieldName: farmName,
        fieldArea: areaInHectares,
        SowingDate: sowingDate,
        cropType: cropType,
        Points: coordinates,
        email: getUserEmail(),
        crop_name: selectedCrop.name,
        mapImageUrl: mapImageUrl,
      };

      const response = await axios.post(
        `${BASE_URL}/api/submit-trial-farm`,
        fieldData,
      );

      const { success, message, field_id } = response.data;

      if (success) {
        // NAVIGATE IMMEDIATELY
        navigate("/user/crop-health-management");

        // RUN BACKGROUND TASKS SEQUENTIALLY
        (async () => {
          try {
            // =========================
            // FIRST API
            // =========================

            await axios.post(`${BASE_URL}/api/user/stats/`, {
              field_id: field_id,
            });

            // =========================
            // SECOND API
            // =========================

            await axios.post(
              `${BASE_URL}/api/user/farm-management/openai-sentinel-remarks`,
              {
                fieldId: field_id,
              },
            );

            console.log("Background farm processing completed");
          } catch (error) {
            console.error("Background processing error:", error);
          }
        })();
      } else {
        if (response?.status === 403) {
          setErrorMessage(
            "You have already added a paid farm, so you are not eligible for the trial farm.",
          );
        } else {
          setErrorMessage(message || "Something went wrong.");
        }
      }
    } catch (err) {
      console.error("Submit error:", err);

      if (err?.response?.status === 403) {
        setErrorMessage(
          err?.response?.data?.message ||
            "You have already added a paid farm, so you are not eligible for the trial farm.",
        );
      } else {
        setErrorMessage(
          err?.response?.data?.message || "Failed to submit. Please try again.",
        );
      }
    } finally {
      setSpinnerLoading(false); // Always hide spinner
    }
  };
  return (
    <div className="trial-add-farm-page">
      {spinnerLoading && (
        <div className="farm-loader-overlay">
          <div className="farm-loader-card">
            <span className="farm-loader-eyebrow">FARMOLOGY TRIAL</span>
            <div className="farm-loader-spinner" />
            <h2>Adding your farm...</h2>
            <p>Preparing your trial farm and farm intelligence.</p>
          </div>
        </div>
      )}

      <section className="trial-page-intro">
        <div>
          <span className="trial-eyebrow">FARMOLOGY TRIAL</span>
          <h1 className="trial-page-title">Add your trial farm.</h1>
          <p className="trial-page-description">
            Set up your farm boundary and crop details to start your Farmology
            trial.
          </p>
        </div>

        <div className="trial-limit-card">
          <span>TRIAL LIMIT</span>
          <strong>Up to 2 hectares</strong>
          <small>One-time trial access</small>
        </div>
      </section>

      <section className="trial-section-card trial-location-card">
        <div className="trial-section-heading">
          <div>
            <span className="trial-section-eyebrow">01 · FARM LOCATION</span>
            <h2>Mark your farm area</h2>
            <p>
              Search your location, then click on the map to draw your farm
              boundary.
            </p>
          </div>
          <span className="trial-map-status">
            <span className="trial-status-dot" />
            Map ready
          </span>
        </div>

        <div className="trial-location-search">
          <TextField
            inputRef={searchBoxRef}
            placeholder="Search location"
            fullWidth
            size="small"
            variant="outlined"
            error={Boolean(SearchErrorMessage)}
            helperText={SearchErrorMessage}
            className="trial-text-field"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOnIcon />
                </InputAdornment>
              ),
            }}
          />

          <LoadingButton
            variant="contained"
            onClick={handleGeocode}
            className="trial-search-button"
          >
            Search
          </LoadingButton>
        </div>

        <div ref={mapRef} className="trial-map" />

        <div className="trial-map-footer">
          <div className="trial-area-display">
            <span className="trial-area-label">SELECTED AREA</span>
            <strong>
              {selectedArea > 0
                ? `${selectedArea} ha`
                : "Mark 3+ points on the map"}
            </strong>
          </div>

          <Button
            variant="outlined"
            onClick={clearAll}
            className="trial-clear-button"
          >
            Clear map
          </Button>
        </div>
      </section>

      <section className="trial-section-card trial-details-card">
        <div className="trial-section-heading">
          <div>
            <span className="trial-section-eyebrow">02 · FARM DETAILS</span>
            <h2>Tell us about your farm</h2>
            <p>
              Add the basic crop and farm information required to create your
              trial.
            </p>
          </div>
        </div>

        <div className="trial-form">
          <div className="trial-form-field">
            <TextField
              label="Farm Name"
              value={farmName}
              onChange={(e) => setFarmName(e.target.value)}
              fullWidth
              className="trial-text-field"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AgricultureIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="trial-form-field">
            <TextField
              label={
                selectedCrop?.name?.toLowerCase() === "tea"
                  ? "Pruning Date"
                  : "Sowing Date"
              }
              type="date"
              value={sowingDate}
              onChange={(e) => setSowingDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              className="trial-text-field"
            />
          </div>

          <div className="trial-form-field">
            <label className="trial-form-label">Crop Type</label>
            <div className="trial-radio-group">
              <FormControlLabel
                value="plantation"
                control={
                  <Radio
                    checked={cropType === "plantation"}
                    onChange={() => setCropType("plantation")}
                  />
                }
                label="🌴 Plantation"
              />
              <FormControlLabel
                value="field"
                control={
                  <Radio
                    checked={cropType === "field"}
                    onChange={() => setCropType("field")}
                  />
                }
                label="🌾 Field Crop"
              />
            </div>
          </div>

          <div className="trial-form-field">
            <label className="trial-form-label">Select Crop</label>
            {loading ? (
              <div className="trial-crop-loading">
                <CircularProgress size={22} />
                <span>Loading crops...</span>
              </div>
            ) : (
              <Select
                value={selectedCrop?.name || ""}
                onChange={(e) =>
                  setSelectedCrop(crops.find((c) => c.name === e.target.value))
                }
                fullWidth
                className="trial-select"
              >
                {crops.length === 0 ? (
                  <MenuItem disabled>No crops available</MenuItem>
                ) : (
                  crops.map((crop) => (
                    <MenuItem key={crop.id} value={crop.name}>
                      🌿 {crop.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            )}
          </div>

          {errorMessage && (
            <div className="trial-error-message" role="alert">
              <strong>Something needs attention</strong>
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="trial-submit-wrap">
            <Tooltip title="Please fill all the fields" arrow>
              <span className="trial-submit-tooltip-wrap">
                <LoadingButton
                  variant="contained"
                  loading={spinnerLoading}
                  onClick={handleSubmit}
                  fullWidth
                  disabled={!farmName || !sowingDate || !selectedCrop}
                  className="trial-save-button"
                >
                  Save Trial Farm
                </LoadingButton>
              </span>
            </Tooltip>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrialAddFarm;
